class MiniCart extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.header = document.querySelector('sticky-header');
    this.drawer = document.querySelector('cart-drawer');
    new IntersectionObserver(this.handleIntersection.bind(this)).observe(this);
  }

  handleIntersection(entries, observer) {
    if (!entries[0].isIntersecting) return;
    observer.unobserve(this);

    fetch(this.dataset.url)
      .then(response => response.text())
      .then(html => {
        document.getElementById('mini-cart').innerHTML =
          this.getSectionInnerHTML(html, '.shopify-section');
          
          document.dispatchEvent(new CustomEvent('cartdrawer:opened'));
      })
      .catch(e => {
        console.error(e);
      });
  }

  open() {
    const detailsElement = this.drawer.querySelector('details');
    this.drawer.setActiveElement(this.activeElement || document.activeElement);

    if (detailsElement.hasAttribute('open')) {
      this.drawer.focusDialog();
      return;
    }

    this.drawer.openMenuDrawer();
    // Let the product form and Ajax-rendered sections settle before asserting
    // the modal's initial focus.
    setTimeout(() => {
      this.drawer.focusDialog();
    }, 150);
  }

  renderContents(parsedState) {
    this.productId = parsedState.id;

    // While the drawer is open (upsell adds, gift wrap, quick view),
    // patch the money-touched regions instead of swapping the whole
    // drawer — keeps open collapsibles, scroll and upsell state intact.
    const drawerOpen = this.drawer && !!this.drawer.querySelector('details[open]');

    this.getSectionsToRender().forEach((section => {
      if (!document.getElementById(section.id)) return;

      if (
        section.id === 'mini-cart' &&
        drawerOpen &&
        window.theme && theme.patchMiniCart &&
        parsedState.sections[section.id] &&
        theme.patchMiniCart(this.getSectionInnerHTML(parsedState.sections[section.id], section.selector))
      ) return;

      document.getElementById(section.id).innerHTML =
        this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    }));

    this.open();
    // This is intentionally anchored to the render completion as well as the
    // drawer class: app scripts can open <details> before MiniCart.open(), but
    // the dialog must still receive initial focus after its Ajax HTML exists.
    setTimeout(() => {
      const dialog = this.drawer.querySelector('[role="dialog"]');
      if (!dialog) return;
      const initialFocus = dialog.querySelector('[data-drawer-force-close]') || dialog;
      dialog.setAttribute('data-focus-trap-active', 'true');
      if (!dialog.hasAttribute('data-keyboard-trap-bound')) {
        dialog.setAttribute('data-keyboard-trap-bound', 'true');
        dialog.addEventListener('keydown', (event) => {
          if (event.key !== 'Tab') return;
          const focusableElements = getFocusableElements(dialog).filter((element) => element.offsetParent !== null);
          const first = focusableElements[0];
          const last = focusableElements[focusableElements.length - 1];
          if (event.shiftKey && document.activeElement === first) {
            event.preventDefault();
            last?.focus();
          } else if (!event.shiftKey && document.activeElement === last) {
            event.preventDefault();
            first?.focus();
          }
        });
      }
      if (typeof trapFocus === 'function') trapFocus(dialog, initialFocus);
      else initialFocus.focus();
    }, 350);
  }

  getSectionsToRender() {
    return [
      {
        id: 'mini-cart',
        section: 'mini-cart',
        selector: '.shopify-section'
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section'
      },
      {
        id: 'mobile-cart-icon-bubble',
        section: 'mobile-cart-icon-bubble',
        selector: '.shopify-section'
      }
    ];
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser()
      .parseFromString(html, 'text/html')
      .querySelector(selector).innerHTML;
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

if (!customElements.get('mini-cart')) customElements.define('mini-cart', MiniCart);

// Some app bundles register the cart elements before this asset executes. Keep
// the accessibility behavior independent of registration order by observing
// the actual drawer state as a final, DOM-level safety net.
(() => {
  const initCartAccessibility = () => {
    const drawer = document.querySelector('cart-drawer');
    const details = drawer?.querySelector('details');
    if (!drawer || !details) return false;
    if (drawer.hasAttribute('data-accessibility-initialized')) return true;
    drawer.setAttribute('data-accessibility-initialized', 'true');

    let opener = null;

    const syncDialogState = (isOpen = drawer.querySelector('details')?.hasAttribute('open')) => {
      const dialog = drawer.querySelector('[role="dialog"]');
      if (!dialog) return;
      if (isOpen) {
        dialog.removeAttribute('aria-hidden');
        dialog.inert = false;
      } else {
        dialog.setAttribute('aria-hidden', 'true');
        dialog.inert = true;
        dialog.removeAttribute('data-focus-trap-active');
      }
    };

    const focusDialog = () => {
      const currentDetails = drawer.querySelector('details');
      const dialog = drawer.querySelector('[role="dialog"]');
      if (!currentDetails?.hasAttribute('open') || !dialog) return;
      syncDialogState(true);
      const initialFocus = dialog.querySelector('[data-drawer-force-close]') || dialog;
      dialog.setAttribute('data-focus-trap-active', 'true');
      if (!dialog.hasAttribute('data-keyboard-trap-bound')) {
        dialog.setAttribute('data-keyboard-trap-bound', 'true');
        dialog.addEventListener('keydown', (event) => {
          if (event.key !== 'Tab') return;
          const focusableElements = getFocusableElements(dialog).filter((element) => element.offsetParent !== null);
          const first = focusableElements[0];
          const last = focusableElements[focusableElements.length - 1];
          if (event.shiftKey && document.activeElement === first) {
            event.preventDefault();
            last?.focus();
          } else if (!event.shiftKey && document.activeElement === last) {
            event.preventDefault();
            first?.focus();
          }
        });
      }
      if (typeof trapFocus === 'function') trapFocus(dialog, initialFocus);
      else initialFocus.focus();
    };

    const scheduleDialogFocus = () => {
      setTimeout(focusDialog, 500);
      setTimeout(focusDialog, 1400);
    };

    const restoreOpenerFocus = (target = opener || drawer.activeElement) => {
      if (!(target instanceof HTMLElement) || !target.isConnected) return;
      const restore = (force = false) => {
        const active = document.activeElement;
        const focusWasDiscarded = !active || active === document.body || active === document.documentElement || drawer.contains(active);
        if (force || focusWasDiscarded) target.focus({ preventScroll: true });
      };
      restore(true);
      requestAnimationFrame(() => requestAnimationFrame(() => restore()));
      [0, 100, 350, 700, 1000].forEach((delay) => setTimeout(() => restore(), delay));
    };

    document.addEventListener('click', (event) => {
      if (!event.target.closest('[name="add"], add-to-cart')) return;
      opener = document.activeElement;
      scheduleDialogFocus();
    }, true);
    document.addEventListener('submit', (event) => {
      if (!event.target.closest('product-form')) return;
      opener = document.activeElement;
      scheduleDialogFocus();
    }, true);

    // Capture Escape before app scripts can consume it. The drawer is rendered
    // asynchronously, so always resolve the current dialog and opener at use time.
    document.addEventListener('keydown', (event) => {
      if (event.key !== 'Escape') return;
      const currentDetails = drawer.querySelector('details[open]');
      const dialog = drawer.querySelector('[role="dialog"]');
      if (!currentDetails || !dialog?.contains(document.activeElement)) return;

      event.preventDefault();
      event.stopImmediatePropagation();
      const returnTarget = opener || drawer.activeElement || currentDetails.querySelector('summary');
      drawer.closeMenuDrawer(event, returnTarget);
      syncDialogState(false);
      restoreOpenerFocus(returnTarget);
    }, true);

    new MutationObserver(() => {
      const isOpen = drawer.querySelector('details')?.hasAttribute('open');
      syncDialogState(isOpen);
      if (isOpen) setTimeout(focusDialog, 400);
    }).observe(drawer, { attributes: true, attributeFilter: ['open'], childList: true, subtree: true });

    drawer.addEventListener('close', () => {
      syncDialogState(false);
      restoreOpenerFocus();
    });
    syncDialogState();
    return true;
  };

  const bootCartAccessibility = () => {
    if (initCartAccessibility()) return;
    const observer = new MutationObserver(() => {
      if (initCartAccessibility()) observer.disconnect();
    });
    observer.observe(document.documentElement, { childList: true, subtree: true });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bootCartAccessibility, { once: true });
  } else {
    bootCartAccessibility();
  }
})();
